import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../contexts/LanguageContext';
import { useServices, useSubservices } from '../hooks/useAirtable';

function groupSubservicesByService(services, subservices) {
  if (!services?.length || !subservices?.length) return [];
  return services.map(service => {
    const serviceId = service.id != null ? Number(service.id) : Number(service.Id);
    const related = (subservices || []).filter(sub => {
      let subServiceId = null;
      if (sub.Subservices != null) subServiceId = Number(sub.Subservices);
      else if (sub.parent_service_id != null) subServiceId = Number(sub.parent_service_id);
      else if (sub.main_service_id != null) subServiceId = Number(sub.main_service_id);
      else if (sub.service_id != null) subServiceId = Number(sub.service_id);
      else if (sub.parent_service != null) subServiceId = Number(sub.parent_service);
      if (subServiceId == null || isNaN(subServiceId)) return false;
      return String(subServiceId) === String(serviceId) || Number(subServiceId) === Number(serviceId);
    });
    return { service, subservices: related };
  });
}

// Accent color for services section - used subtly across cards and panel
const SERVICE_ACCENT = '#27AAE1';
const SERVICE_ACCENT_LIGHT = 'rgba(39, 170, 225, 0.08)';
const SERVICE_ACCENT_BORDER = 'rgba(39, 170, 225, 0.2)';
const SERVICE_ACCENT_SHADOW = 'rgba(39, 170, 225, 0.15)';

// Helper function to strip HTML tags and decode HTML entities
const stripHtmlTags = (html) => {
  if (!html || typeof html !== 'string') return '';
  
  return html
    // Remove all HTML tags
    .replace(/<[^>]*>/g, '')
    // Decode HTML entities
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&apos;/g, "'")
    // Clean up extra whitespace
    .replace(/\s+/g, ' ')
    .trim();
};

export default function FeaturesSection({ onOpenChat }) {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { currentLanguage, isRTL } = useLanguage()
  
  const { services, loading: servicesLoading, error: servicesError } = useServices(true, currentLanguage);
  const { subservices = [] } = useSubservices(null, true, currentLanguage);
  const servicesWithSubs = useMemo(() => groupSubservicesByService(services, subservices), [services, subservices]);
  const [expandedServiceId, setExpandedServiceId] = useState(null);

  const expandedData = useMemo(() => {
    if (expandedServiceId == null || !Array.isArray(servicesWithSubs)) return null;
    return servicesWithSubs.find(({ service }) => String(service?.id ?? service?.Id ?? '') === String(expandedServiceId)) || null;
  }, [servicesWithSubs, expandedServiceId]);
  const getSubserviceTitle = (s) => s?.title || s?.name || s?.title_en || s?.title_ar || '';

  // Debug logging for services
  useEffect(() => {
    if (services.length > 0) {
      console.log('🔧 Services loaded from database:', {
        count: services.length,
        services: services.map(s => ({
          id: s.id,
          name: s.name,
          title: s.title,
          status: s.status,
          active: s.active
        }))
      });
    }
  }, [services]);

  // Enhanced loading state with shimmer effect
  if (servicesLoading) {
    return (
      <section id="services" style={{ padding: "80px 0", position: "relative" }}>
        <div style={{ maxWidth: "1200px", margin: "0 auto", padding: "0 20px" }}>
          {/* Shimmer loading skeleton */}
          <div style={{ textAlign: "center", marginBottom: "48px" }}>
            <div 
              style={{
                height: "32px",
                width: "300px",
                margin: "0 auto 16px auto",
                borderRadius: "4px",
                background: "linear-gradient(90deg, #e0e0e0 25%, #f0f0f0 50%, #e0e0e0 75%)",
                backgroundSize: "200% 100%",
                animation: "shimmer 2s infinite"
              }}
            />
            <div 
              style={{
                height: "16px",
                width: "200px",
                margin: "0 auto",
                borderRadius: "4px",
                background: "linear-gradient(90deg, #e0e0e0 25%, #f0f0f0 50%, #e0e0e0 75%)",
                backgroundSize: "200% 100%",
                animation: "shimmer 2s infinite"
              }}
            />
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: "32px" }}>
            {[...Array(6)].map((_, i) => (
              <div key={i} style={{ background: "white", borderRadius: "8px", padding: "24px", boxShadow: "0 1px 3px rgba(0,0,0,0.1)" }}>
                <div 
                  style={{
                    height: "24px",
                    marginBottom: "16px",
                    borderRadius: "4px",
                    background: "linear-gradient(90deg, #e0e0e0 25%, #f0f0f0 50%, #e0e0e0 75%)",
                    backgroundSize: "200% 100%",
                    animation: "shimmer 2s infinite",
                    animationDelay: `${i * 0.1}s`
                  }}
                />
                <div 
                  style={{
                    height: "16px",
                    marginBottom: "8px",
                    borderRadius: "4px",
                    background: "linear-gradient(90deg, #e0e0e0 25%, #f0f0f0 50%, #e0e0e0 75%)",
                    backgroundSize: "200% 100%",
                    animation: "shimmer 2s infinite",
                    animationDelay: `${i * 0.1}s`
                  }}
                />
                <div 
                  style={{
                    height: "16px",
                    width: "70%",
                    borderRadius: "4px",
                    background: "linear-gradient(90deg, #e0e0e0 25%, #f0f0f0 50%, #e0e0e0 75%)",
                    backgroundSize: "200% 100%",
                    animation: "shimmer 2s infinite",
                    animationDelay: `${i * 0.1}s`
                  }}
                />
              </div>
            ))}
          </div>
        </div>
        <style>{`
          @keyframes shimmer {
            0% { background-position: -200% 0; }
            100% { background-position: 200% 0; }
          }
        `}</style>
      </section>
    );
  }

  // Use only database services - no fallback to static data
  // Only show services that exist in the database
  const serviceCategories = services;

  const handleServiceClick = (service) => {
    if (!service?.id && service?.Id == null) return;
    const id = service.id ?? service.Id;
    setExpandedServiceId(prev => (prev === id ? null : id));
  }

  return (
    <>
      <section
        id="services"
        style={{
          display: "block",
          padding: "clamp(80px, 12vh, 120px) 0px clamp(80px, 12vh, 120px) 0px",
          margin: "0px",
          width: "100%",
          position: "relative",
          background: "transparent",
          maxWidth: "100%",
        }}
      >
        <div
          className="container container--large"
          style={{
            color: "rgb(26, 26, 26)",
            width: "100%",
            boxSizing: "border-box",
            maxWidth: "1200px",
            margin: "0px auto",
            padding: "0 clamp(15px, 4vw, 20px)",
          }}
        >
          <div>
            {/* Header Section */}
            <div
              className="rte content content--left space-bottom-sm"
              style={{
                fontFamily: "ff-real-text-pro, sans-serif",
                fontSize: "16px",
                fontWeight: 300,
                WebkitFontSmoothing: "antialiased",
                maxWidth: "100%",
                boxSizing: "border-box",
                marginLeft: "0px",
                marginRight: "auto",
                marginTop: "0px",
                marginBottom: "40px",
                padding: "0px",
                textAlign: "center",
              }}
            >
              <h2
                style={{
                  fontFamily: "ff-real-text-pro, sans-serif",
                  fontStyle: "normal",
                  fontWeight: 300,
                  letterSpacing: "-0.025em",
                  lineHeight: "1.3em",
                  marginLeft: "0px",
                  marginTop: "0px",
                  marginBottom: "15px",
                  fontSize: "clamp(30px, 5vw, 50px)",
                  color: "rgb(26, 26, 26)",
                }}
              >
                {t('features.title')}
              </h2>
              <p
                style={{
                  fontFamily: "Merriweather",
                  fontSize: "18px",
                  fontStyle: "normal",
                  fontWeight: 300,
                  lineHeight: "1.66em",
                  marginTop: "0px",
                  marginBottom: "30px",
                  color: "rgb(26, 26, 26)",
                  maxWidth: "800px",
                  margin: "0 auto 30px auto"
                }}
              >
                {t('features.subtitle')}
              </p>
            </div>

            {/* Main Category Cards - Only show if services exist in database */}
            {serviceCategories.length > 0 ? (
              <div
                className="services-main-grid"
                style={{
                  display: "grid",
                  gridTemplateColumns: `repeat(${Math.min(serviceCategories.length, 4)}, 1fr)`,
                  gap: "20px",
                  marginBottom: "30px",
                  transition: "all 0.5s ease",
                  width: "100%",
                  boxSizing: "border-box"
                }}
              >
                {serviceCategories.map((service) => (
                <div
                  key={service.id}
                  className="service-card-main"
                  style={{
                    position: "relative",
                    borderRadius: "16px",
                    overflow: "hidden",
                    height: "110px",
                    backgroundColor: "#000000",
                    cursor: "pointer",
                    transition: "background-color 0.4s ease, transform 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94), box-shadow 0.4s ease",
                    boxShadow: "0 6px 25px rgba(0, 0, 0, 0.2)",
                    transform: "translateY(0)"
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = SERVICE_ACCENT
                    e.currentTarget.style.transform = 'translateY(-5px)'
                    e.currentTarget.style.boxShadow = `0 12px 40px ${SERVICE_ACCENT_SHADOW}`
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#000000'
                    e.currentTarget.style.transform = 'translateY(0)'
                    e.currentTarget.style.boxShadow = '0 6px 25px rgba(0, 0, 0, 0.2)'
                  }}
                  onClick={() => handleServiceClick(service)}
                >
                  {/* عنوان واحد: عادي أسفل، وفي الهوفر ينتقل لأعلى والزر يظهر تحته */}
                  <div
                    className="service-card-content"
                    style={{
                      position: 'absolute',
                      top: '58%',
                      left: '50%',
                      width: '92%',
                      maxWidth: '92%',
                      zIndex: 2,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      textAlign: 'center',
                      transform: 'translate(-50%, -50%)',
                      transition: 'top 0.35s ease, left 0.35s ease, width 0.35s ease, transform 0.35s ease'
                    }}
                  >
                    <h3 style={{
                      margin: "0px",
                      fontFamily: "Inter, sans-serif",
                      fontSize: "clamp(14px, 2.5vw, 18px)",
                      fontWeight: 600,
                      lineHeight: "1.3em",
                      color: "white",
                      letterSpacing: "-0.025em",
                      textShadow: "0 2px 8px rgba(0, 0, 0, 0.5)",
                      wordBreak: "break-word"
                    }}>
                      {service.title}
                    </h3>
                    <span
                      className="service-card-explain-btn"
                      style={{
                        display: "inline-block",
                        marginTop: "10px",
                        padding: "6px 14px",
                        fontFamily: "Inter, sans-serif",
                        fontSize: "12px",
                        fontWeight: 500,
                        color: "white",
                        background: "rgba(255,255,255,0.2)",
                        border: "1px solid rgba(255,255,255,0.6)",
                        borderRadius: "8px",
                        cursor: "pointer",
                        opacity: 0,
                        pointerEvents: "none",
                        transition: "opacity 0.3s ease, background 0.2s ease, border-color 0.2s ease"
                      }}
                    >
                      {currentLanguage === 'ar' ? 'شرح' : 'View details'}
                    </span>
                  </div>
                </div>
              ))}
              </div>
            ) : (
              // Show message if no services found in database
              <div style={{
                textAlign: "center",
                padding: "60px 20px",
                color: "rgb(26, 26, 26)"
              }}>
                <p style={{
                  fontFamily: "Inter, sans-serif",
                  fontSize: "18px",
                  fontWeight: 400,
                  color: "rgba(26, 26, 26, 0.7)",
                  margin: 0
                }}>
                  {currentLanguage === 'ar' 
                    ? 'لا توجد خدمات متاحة حالياً' 
                    : 'No services available at the moment'}
                </p>
              </div>
            )}

            {/* Expanded subservices panel - responsive when a service is clicked */}
            {expandedData && (
              <div
                className="services-expanded-panel"
                style={{
                  marginTop: "24px",
                  padding: "clamp(16px, 4vw, 28px)",
                  background: `linear-gradient(135deg, ${SERVICE_ACCENT_LIGHT} 0%, rgba(255,255,255,0.98) 100%)`,
                  borderRadius: "16px",
                  border: `1px solid ${SERVICE_ACCENT_BORDER}`,
                  borderInlineStartWidth: "4px",
                  borderInlineStartColor: SERVICE_ACCENT,
                  width: "100%",
                  boxSizing: "border-box",
                  animation: "fadeIn 0.3s ease"
                }}
              >
                <div style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  flexWrap: "wrap",
                  gap: "12px",
                  marginBottom: "20px",
                  paddingBottom: "16px",
                  borderBottom: `1px solid ${SERVICE_ACCENT_BORDER}`
                }}>
                  <h3 style={{
                    margin: 0,
                    fontFamily: "Inter, sans-serif",
                    fontSize: "clamp(18px, 3vw, 22px)",
                    fontWeight: 600,
                    color: "rgb(26, 26, 26)"
                  }}>
                    {expandedData.service?.title}
                  </h3>
                  <button
                    type="button"
                    className="services-close-btn"
                    onClick={() => setExpandedServiceId(null)}
                    style={{
                      padding: "8px 16px",
                      fontFamily: "Inter, sans-serif",
                      fontSize: "14px",
                      fontWeight: 500,
                      color: "rgb(26, 26, 26)",
                      background: "white",
                      border: `1px solid ${SERVICE_ACCENT_BORDER}`,
                      borderRadius: "8px",
                      cursor: "pointer",
                      boxShadow: "0 1px 3px rgba(0,0,0,0.06)"
                    }}
                  >
                    {currentLanguage === 'ar' ? 'إغلاق' : 'Close'}
                  </button>
                </div>
                {expandedData.subservices?.length > 0 ? (
                  <div
                    className="services-subservices-grid"
                    style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(auto-fill, minmax(min(100%, 280px), 1fr))",
                      gap: "16px",
                      width: "100%",
                      boxSizing: "border-box"
                    }}
                  >
                    {expandedData.subservices.map((sub) => (
                      <Link
                        key={sub.id}
                        to={`/${currentLanguage}/subservice/${sub.id}`}
                        className="services-subservice-card"
                        style={{
                          display: "block",
                          padding: "18px 20px",
                          background: "white",
                          borderRadius: "12px",
                          border: `1px solid ${SERVICE_ACCENT_BORDER}`,
                          borderInlineStart: `3px solid ${SERVICE_ACCENT}`,
                          boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                          textDecoration: "none",
                          color: "inherit",
                          transition: "box-shadow 0.2s ease, transform 0.2s ease, border-color 0.2s ease"
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.boxShadow = `0 6px 20px ${SERVICE_ACCENT_SHADOW}`;
                          e.currentTarget.style.transform = 'translateY(-2px)';
                          e.currentTarget.style.borderColor = SERVICE_ACCENT;
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.04)';
                          e.currentTarget.style.transform = 'translateY(0)';
                          e.currentTarget.style.borderColor = SERVICE_ACCENT_BORDER;
                        }}
                      >
                        <div style={{
                          fontFamily: "Inter, sans-serif",
                          fontSize: "16px",
                          fontWeight: 600,
                          color: "rgb(26, 26, 26)",
                          marginBottom: "6px",
                          lineHeight: 1.3
                        }}>
                          {getSubserviceTitle(sub)}
                        </div>
                        {sub.description && (
                          <p style={{
                            margin: 0,
                            fontFamily: "Inter, sans-serif",
                            fontSize: "13px",
                            color: "rgba(26, 26, 26, 0.7)",
                            lineHeight: 1.45,
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden"
                          }}>
                            {stripHtmlTags(sub.description)}
                          </p>
                        )}
                      </Link>
                    ))}
                  </div>
                ) : (
                  <p style={{
                    margin: 0,
                    fontFamily: "Inter, sans-serif",
                    fontSize: "15px",
                    color: "rgba(26, 26, 26, 0.6)"
                  }}>
                    {currentLanguage === 'ar' ? 'لا توجد خدمات فرعية لهذه الخدمة.' : 'No subservices for this service.'}
                  </p>
                )}
              </div>
            )}

          </div>
        </div>
      </section>
      
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        .service-card-main:hover .service-card-content {
          bottom: auto !important;
          top: 50% !important;
          left: 50% !important;
          width: 92% !important;
          max-width: 92% !important;
          transform: translate(-50%, -50%) !important;
        }
        .service-card-main:hover .service-card-explain-btn {
          opacity: 1 !important;
          pointer-events: auto !important;
        }
        .service-card-explain-btn:hover {
          background: rgba(255,255,255,0.35) !important;
          border-color: white !important;
        }

        .services-close-btn:hover {
          border-color: #27AAE1 !important;
          color: #27AAE1 !important;
          box-shadow: 0 2px 8px rgba(39, 170, 225, 0.2) !important;
        }

        /* Subservice card title subtle accent on hover */
        .services-subservice-card:hover > div:first-of-type {
          color: #27AAE1;
        }

        /* ========== جوال فقط: تحسين عرض الخدمات ========== */
        @media (max-width: 768px) {
          #services {
            padding: 50px 0 60px !important;
          }
          #services .container.container--large {
            padding-left: 16px !important;
            padding-right: 16px !important;
          }
          #services .content.space-bottom-sm {
            margin-bottom: 28px !important;
          }
          #services .content h2 {
            font-size: clamp(26px, 6vw, 34px) !important;
            margin-bottom: 10px !important;
          }
          #services .content p {
            font-size: 15px !important;
            margin-bottom: 24px !important;
          }
          .services-main-grid {
            grid-template-columns: 1fr 1fr !important;
            gap: 14px !important;
            width: 100% !important;
            margin-bottom: 24px !important;
          }
          .services-main-grid > .service-card-main {
            height: 118px !important;
            min-height: 118px !important;
            border-radius: 14px !important;
            box-shadow: 0 4px 16px rgba(0,0,0,0.15) !important;
          }
          .services-main-grid .service-card-content {
            top: 50% !important;
            left: 50% !important;
            width: 88% !important;
            max-width: 88% !important;
            transform: translate(-50%, -50%) !important;
            padding: 0 6px !important;
          }
          .services-main-grid .service-card-content h3 {
            font-size: 13px !important;
            line-height: 1.35 !important;
            margin: 0 !important;
            text-align: center !important;
            padding: 0 !important;
            display: -webkit-box !important;
            -webkit-line-clamp: 3 !important;
            -webkit-box-orient: vertical !important;
            overflow: hidden !important;
            word-break: break-word !important;
          }
          .services-main-grid .service-card-explain-btn {
            display: none !important;
          }
          .services-expanded-panel {
            padding: 18px 14px !important;
            margin-top: 20px !important;
            border-radius: 14px !important;
          }
          .services-expanded-panel > div:first-of-type {
            margin-bottom: 16px !important;
            padding-bottom: 14px !important;
            flex-direction: column !important;
            align-items: stretch !important;
            gap: 12px !important;
          }
          .services-expanded-panel h3 {
            font-size: 18px !important;
            text-align: center !important;
          }
          .services-expanded-panel .services-close-btn {
            width: 100% !important;
            padding: 10px 16px !important;
            font-size: 15px !important;
          }
          .services-subservices-grid {
            grid-template-columns: 1fr !important;
            gap: 12px !important;
          }
          .services-subservice-card {
            padding: 16px 18px !important;
            min-height: 52px !important;
            border-radius: 12px !important;
          }
          .services-subservice-card > div:first-of-type {
            font-size: 15px !important;
            margin-bottom: 6px !important;
          }
          .services-subservice-card p {
            font-size: 13px !important;
            -webkit-line-clamp: 2 !important;
          }
        }

        /* جوال صغير: عمود واحد لراحة القراءة */
        @media (max-width: 480px) {
          #services {
            padding: 40px 0 50px !important;
          }
          #services .container.container--large {
            padding-left: 14px !important;
            padding-right: 14px !important;
          }
          .services-main-grid {
            grid-template-columns: 1fr !important;
            gap: 14px !important;
          }
          .services-main-grid > .service-card-main {
            height: 100px !important;
            min-height: 100px !important;
            border-radius: 12px !important;
          }
          .services-main-grid .service-card-content h3 {
            font-size: 14px !important;
            -webkit-line-clamp: 2 !important;
          }
          .services-expanded-panel {
            padding: 16px 12px !important;
          }
          .services-subservice-card {
            padding: 14px 16px !important;
          }
        }

        /* شاشات صغيرة جداً (مثل iPhone SE) */
        @media (max-width: 375px) {
          .services-main-grid > .service-card-main {
            height: 94px !important;
            min-height: 94px !important;
          }
          .services-main-grid .service-card-content h3 {
            font-size: 12px !important;
          }
        }
        
        /* Tablet - لا تغيير عن السلوك الحالي */
        @media (max-width: 1024px) and (min-width: 769px) {
          .services-main-grid {
            grid-template-columns: repeat(2, 1fr) !important;
            gap: 15px !important;
          }
        }
        
        /* Ultra wide screens */
        @media (min-width: 1400px) {
          .services-main-grid {
            grid-template-columns: repeat(4, 1fr) !important;
            gap: 25px !important;
          }
        }
      `}</style>
    </>
  );
}